import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listInvites,
  bulkWhitelist,
  revokeWhitelistInvite,
  resendWhitelistInvite,
} from "@/lib/whitelist.functions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";
import { Trash2, Send, Plus } from "lucide-react";

export const Route = createFileRoute("/admin/whitelist")({
  head: () => ({
    meta: [
      { title: "Whitelist & Onboarding — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Whitelist" nav={ADMIN_NAV}>
        <WhitelistPanel />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface InviteRow {
  id: string;
  email: string;
  intended_role: "client" | "agent" | "staff" | string;
  source: string | null;
  note: string | null;
  sent_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
}

interface ManualRow {
  email: string;
  fullName: string;
  role: "client" | "agent" | "staff";
  phone: string;
  note: string;
}

const emptyRow = (): ManualRow => ({
  email: "",
  fullName: "",
  role: "client",
  phone: "",
  note: "",
});

function statusOf(r: InviteRow): { label: string; cls: string } {
  if (r.revoked_at) return { label: "Revoked", cls: "bg-danger/15 text-danger" };
  if (r.accepted_at) return { label: "Accepted", cls: "bg-success/15 text-success" };
  return { label: "Pending", cls: "bg-bronze-400/20 text-bronze-600" };
}

function parseCsv(text: string): ManualRow[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];
  // Detect header
  const first = lines[0].toLowerCase();
  const hasHeader = first.includes("email");
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const rows: ManualRow[] = [];
  for (const line of dataLines) {
    const cells = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    const [email = "", fullName = "", roleRaw = "client", phone = "", note = ""] = cells;
    if (!email) continue;
    const rl = roleRaw.toLowerCase();
  const role = rl === "agent" ? "agent" : rl === "staff" ? "staff" : "client";
    rows.push({ email, fullName, role, phone, note });
  }
  return rows;
}

function WhitelistPanel() {
  const list = useServerFn(listInvites);
  const bulk = useServerFn(bulkWhitelist);
  const revoke = useServerFn(revokeWhitelistInvite);
  const resend = useServerFn(resendWhitelistInvite);

  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "accepted" | "revoked">("all");
  const [roleFilter, setRoleFilter] = useState<"all" | "client" | "agent" | "staff">("all");
  const [search, setSearch] = useState("");
  const [manualRows, setManualRows] = useState<ManualRow[]>([emptyRow()]);
  const [csvText, setCsvText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const res = await list();
      setInvites(res.invites as InviteRow[]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load invites");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  const filtered = useMemo(() => invites.filter((r) => {
    if (roleFilter !== "all" && r.intended_role !== roleFilter) return false;
    const s = statusOf(r).label.toLowerCase();
    if (filter !== "all" && s !== filter) return false;
    if (search && !r.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [invites, filter, roleFilter, search]);

  function updateManual(i: number, patch: Partial<ManualRow>) {
    setManualRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() { setManualRows((r) => [...r, emptyRow()]); }
  function removeRow(i: number) { setManualRows((r) => r.filter((_, idx) => idx !== i)); }

  async function submitManual() {
    const rows = manualRows
      .filter((r) => r.email.trim())
      .map((r) => ({
        email: r.email.trim(),
        fullName: r.fullName.trim() || null,
        role: r.role,
        phone: r.phone.trim() || null,
        note: r.note.trim() || null,
      }));
    if (rows.length === 0) return toast.error("Add at least one email.");
    await runBulk(rows);
  }

  async function submitCsv() {
    const parsed = parseCsv(csvText);
    if (parsed.length === 0) return toast.error("No rows detected.");
    const rows = parsed.map((r) => ({
      email: r.email,
      fullName: r.fullName || null,
      role: r.role,
      phone: r.phone || null,
      note: r.note || null,
    }));
    await runBulk(rows);
  }

  async function runBulk(rows: Array<{
    email: string; fullName: string | null; role: "client" | "agent" | "staff"; phone: string | null; note: string | null;
  }>) {
    setSubmitting(true);
    try {
      const res = await bulk({ data: { rows } });
      const ok = res.results.filter((r) => r.status === "invited").length;
      const errs = res.results.filter((r) => r.status === "error");
      toast.success(`Invited ${ok} of ${res.results.length}`);
      if (errs.length > 0) {
        toast.error(`${errs.length} failed — ${errs[0].email}: ${errs[0].message ?? "error"}`);
      }
      setManualRows([emptyRow()]);
      setCsvText("");
      void refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk invite failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onRevoke(id: string) {
    try {
      await revoke({ data: { inviteId: id } });
      toast.success("Invite revoked");
      void refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Revoke failed");
    }
  }

  async function onResend(id: string) {
    try {
      await resend({ data: { inviteId: id } });
      toast.success("Recovery link sent");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Resend failed");
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <h2 className="font-display font-semibold text-navy-800" style={{ fontSize: "32px", letterSpacing: "-0.01em" }}>
          Whitelist <em className="text-bronze-600 italic font-normal">& Onboard</em>
        </h2>
        <p className="mt-2 font-sans text-[13px] text-charcoal-500 max-w-2xl">
          Pre-approve clients and strategists. Each invite emails a secure setup link, pre-creates their profile and role,
          and lets them walk straight into a populated portal.
        </p>
      </div>

      <div className="bg-white border border-bronze-600/20 rounded-[4px] shadow-soft p-6">
        <Tabs defaultValue="manual">
          <TabsList className="bg-cream-50">
            <TabsTrigger value="manual">Manual rows</TabsTrigger>
            <TabsTrigger value="csv">Bulk CSV</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-4 space-y-3">
            <div className="grid grid-cols-[1.4fr_1.2fr_0.8fr_1fr_1.4fr_auto] gap-2 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              <div>Email</div><div>Full name</div><div>Role</div><div>Phone</div><div>Note</div><div></div>
            </div>
            {manualRows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1.4fr_1.2fr_0.8fr_1fr_1.4fr_auto] gap-2">
                <Input value={r.email} onChange={(e) => updateManual(i, { email: e.target.value })}
                  placeholder="name@example.com" className="border-bronze-600/30 bg-white" />
                <Input value={r.fullName} onChange={(e) => updateManual(i, { fullName: e.target.value })}
                  placeholder="Jane Doe" className="border-bronze-600/30 bg-white" />
                <Select value={r.role} onValueChange={(v) => updateManual(i, { role: v as "client" | "agent" | "staff" })}>
                  <SelectTrigger className="border-bronze-600/30 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="agent">Strategist</SelectItem>
                    <SelectItem value="staff">Operations Staff</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={r.phone} onChange={(e) => updateManual(i, { phone: e.target.value })}
                  placeholder="(optional)" className="border-bronze-600/30 bg-white" />
                <Input value={r.note} onChange={(e) => updateManual(i, { note: e.target.value })}
                  placeholder="(optional)" className="border-bronze-600/30 bg-white" />
                <button type="button" onClick={() => removeRow(i)} disabled={manualRows.length === 1}
                  className="text-charcoal-500 hover:text-danger disabled:opacity-30 px-2">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={addRow}
                className="inline-flex items-center gap-1.5 font-sans text-[12px] text-bronze-600 hover:text-bronze-400">
                <Plus size={14} /> Add another
              </button>
              <PrimaryButton onClick={() => void submitManual()} disabled={submitting}>
                {submitting ? "Sending…" : "Send invitations"}
              </PrimaryButton>
            </div>
          </TabsContent>

          <TabsContent value="csv" className="mt-4 space-y-3">
            <p className="font-sans text-[12px] text-charcoal-500">
              Columns (in order): <code className="text-navy-800">email, full_name, role, phone, note</code>.
              Header row optional. Role must be <em>client</em> or <em>agent</em>.
            </p>
            <Textarea
              value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={10}
              placeholder={"email,full_name,role,phone,note\njane@example.com,Jane Doe,client,555-0100,Referral note\njohn@example.com,John Smith,agent,,"}
              className="font-mono text-[12px] border-bronze-600/30 bg-white"
            />
            <div className="flex justify-end">
              <PrimaryButton onClick={() => void submitCsv()} disabled={submitting}>
                {submitting ? "Sending…" : "Import & invite"}
              </PrimaryButton>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <div className="bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-b border-bronze-600/20">
          <h3 className="font-display text-[18px] text-navy-800 mr-auto">Invitations</h3>
          <Select value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
            <SelectTrigger className="w-36 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v as typeof roleFilter)}>
            <SelectTrigger className="w-36 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="agent">Strategists</SelectItem>
              <SelectItem value="staff">Operations Staff</SelectItem>
            </SelectContent>
          </Select>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email…"
            className="w-56 bg-white border-bronze-600/30" />
        </div>
        <table className="w-full">
          <thead className="bg-cream-50 border-b border-bronze-600/20">
            <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Sent</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Note</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bronze-600/15">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-sans text-[13px] text-charcoal-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-sans text-[13px] text-charcoal-500">No invitations.</td></tr>
            )}
            {filtered.map((r) => {
              const s = statusOf(r);
              const pending = !r.accepted_at && !r.revoked_at;
              return (
                <tr key={r.id} className="hover:bg-cream-50/60">
                  <td className="px-4 py-3 font-sans text-[13px] text-charcoal-900">{r.email}</td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900 capitalize">{r.intended_role}</td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-500">
                    {new Date(r.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-[0.12em] ${s.cls}`}>
                      {s.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-500 max-w-xs truncate">{r.note ?? "—"}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {pending && (
                      <>
                        <button type="button" onClick={() => void onResend(r.id)}
                          className="inline-flex items-center gap-1 font-sans text-[11px] text-navy-800 hover:text-bronze-600">
                          <Send size={12} /> Resend
                        </button>
                        <button type="button" onClick={() => void onRevoke(r.id)}
                          className="inline-flex items-center gap-1 font-sans text-[11px] text-danger hover:opacity-80">
                          <Trash2 size={12} /> Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}